import { FormControl, FormArray } from "@angular/forms";

/**
 * Interface for Frequency Type Values
 */
export interface QPilotFrequencyTypeValues {
  FrequencyType: string;
  DisplayName: string | null;
  Value: number;
}

/**
 * Interface for Customer Portal Site Settings - API Data
 * Based on the legacy QPilotCustomerPortalSiteSettings from old QMC
 */
export interface CustomerPortalSiteSettings {
  // General Settings
  IsShowingNotes: boolean;
  IsShowCoupons: boolean;
  AllowRetryScheduledOrder: boolean;

  // Frequency Settings
  ManageAllowedFrequencies: boolean;
  AllowedFrequencies: QPilotFrequencyTypeValues[];

  // Product Settings
  SortProductsAlphabetically: boolean;
  HideOutOfStockProducts: boolean;

  // Styling Settings
  IsStackedButtons: boolean;
  IsDisplayScheduledOrderId: boolean;
  EnableProductCarouselAutoSlide: boolean;
  ShowProductTitleOnImages: boolean;

  // No Scheduled Order Settings
  NoScheduleOrdersBody: string;
  NoScheduleOrdersLink: string;

  // Style Configuration
  Styles: CustomerPortalStyles;
  DefaultStyles: CustomerPortalStyles;

  // Detailed Summary Component Configuration
  DetailedSummaryComponentConfig: DetailedSummaryConfig;
}

/**
 * Interface for Customer Portal Styling Configuration
 */
export interface CustomerPortalStyles {
  // Color Scheme
  PrimaryColor: string;
  OnPrimaryColor: string;
  SurfaceColor: string;
  OnSurfaceColor: string;
  ErrorColor: string;
  OnErrorColor: string;

  // Discount Bar Colors
  DiscountBarColor: string;
  OnDiscountBarColor: string;

  // Button Styling
  ButtonBorderThicknessPx: number;
  ButtonBorderRadiusPx: number;
  ButtonBorderColor: string;
  SelectedButtonBackgroundColor: string;
  SelectedButtonTextColor: string;
  UnselectedButtonTextColor: string;

  // Image Dimensions
  MaxImageWidthPx: number;
  MaxImageHeightPx: number;

  // Custom CSS
  CustomStyles: string;
}

/**
 * Interface for Detailed Summary Component Configuration
 */
export interface DetailedSummaryConfig {
  CustomCss: string;
  Components: DetailedSummaryComponent[];
  /**
   * Unified ordering for ALL components (standard + custom).
   * PRIMARY SOURCE OF TRUTH for component ordering.
   * V2 Portal reads this to determine display order.
   * Defaults to null for backward compatibility - QMC will populate on first save.
   */
  UnifiedOrder?: UnifiedComponentOrderItem[] | null;
  /**
   * Custom component HTML/CSS content created by merchants.
   * Contains the actual content to render.
   * Defaults to null for backward compatibility.
   */
  CustomComponents?: CustomComponentContent[] | null;
}

/**
 * Unified component order item - matches API model
 * Used for ordering both standard and custom components together
 */
export interface UnifiedComponentOrderItem {
  /** Unique identifier: "detailed-summary" for standard, "custom-{uuid}" for custom */
  Identifier: string;
  /** Display name shown in QMC and used for accessibility */
  Label: string;
  /** Position in the display order (0-based index) */
  Order: number;
  /** Whether the component is visible in the portal */
  Enabled: boolean;
  /** true = custom component, false = standard component */
  IsCustom: boolean;
}

/**
 * Custom component content - matches API model
 * Contains the actual HTML/CSS content for custom components
 */
export interface CustomComponentContent {
  /** UUID in format "custom-{guid}" */
  Id: string;
  /** Display name (e.g., "Promotional Banner") */
  Name: string;
  /** Sanitized HTML content */
  HtmlContent: string;
  /** Optional CSS styles for the component */
  CssContent: string;
  /** Whether this component is enabled */
  Enabled: boolean;
  /** Display order */
  Order: number;
  /** When the component was created (ISO string) */
  CreatedAt?: string | null;
  /** When the component was last modified (ISO string) */
  UpdatedAt?: string | null;
}

/**
 * Interface for Individual Detailed Summary Component
 */
export interface DetailedSummaryComponent {
  Order: number;
  Label: string;
  Identifier: string;
  Enabled: boolean;
  Column?: string; // Optional for backward compatibility
}

/**
 * UI Data interface - camelCase for form binding
 */
export interface CustomerPortalSettingsData {
  // General Settings
  isShowingNotes: boolean;
  isShowCoupons: boolean;
  allowRetryScheduledOrder: boolean;

  // Frequency Settings
  manageAllowedFrequencies: boolean;
  allowedFrequencies: QPilotFrequencyTypeValues[];

  // Product Settings
  sortProductsAlphabetically: boolean;
  hideOutOfStockProducts: boolean;

  // Styling Settings
  isStackedButtons: boolean;
  isDisplayScheduledOrderId: boolean;
  enableProductCarouselAutoSlide: boolean;
  showProductTitleOnImages: boolean;

  // No Scheduled Order Settings
  noScheduleOrdersBody: string;
  noScheduleOrdersLink: string;

  // Color Settings
  primaryColor: string;
  onPrimaryColor: string;
  surfaceColor: string;
  onSurfaceColor: string;
  errorColor: string;
  onErrorColor: string;
  discountBarColor: string;
  onDiscountBarColor: string;

  // Button Settings
  buttonBorderThicknessPx: number;
  buttonBorderRadiusPx: number;
  buttonBorderColor: string;
  selectedButtonBackgroundColor: string;
  selectedButtonTextColor: string;
  unselectedButtonTextColor: string;

  // Image Settings
  maxImageWidthPx: number;
  maxImageHeightPx: number;

  // Custom Styles
  customStyles: string;
  detailedSummaryCustomCss: string;
}

/**
 * Form interface with FormControl types for reactive forms
 */
export interface CustomerPortalSettingsForm {
  // General Settings
  isShowingNotes: FormControl<boolean>;
  isShowCoupons: FormControl<boolean>;
  allowRetryScheduledOrder: FormControl<boolean>;

  // Frequency Settings
  manageAllowedFrequencies: FormControl<boolean>;
  allowedFrequencies: FormArray<FormControl<QPilotFrequencyTypeValues>>;

  // Product Settings
  sortProductsAlphabetically: FormControl<boolean>;
  hideOutOfStockProducts: FormControl<boolean>;

  // Styling Settings
  isStackedButtons: FormControl<boolean>;
  isDisplayScheduledOrderId: FormControl<boolean>;
  enableProductCarouselAutoSlide: FormControl<boolean>;
  showProductTitleOnImages: FormControl<boolean>;

  // No Scheduled Order Settings
  noScheduleOrdersBody: FormControl<string>;
  noScheduleOrdersLink: FormControl<string>;

  // Color Settings
  primaryColor: FormControl<string>;
  onPrimaryColor: FormControl<string>;
  surfaceColor: FormControl<string>;
  onSurfaceColor: FormControl<string>;
  errorColor: FormControl<string>;
  onErrorColor: FormControl<string>;
  discountBarColor: FormControl<string>;
  onDiscountBarColor: FormControl<string>;

  // Button Settings
  buttonBorderThicknessPx: FormControl<number>;
  buttonBorderRadiusPx: FormControl<number>;
  buttonBorderColor: FormControl<string>;
  selectedButtonBackgroundColor: FormControl<string>;
  selectedButtonTextColor: FormControl<string>;
  unselectedButtonTextColor: FormControl<string>;

  // Image Settings
  maxImageWidthPx: FormControl<number>;
  maxImageHeightPx: FormControl<number>;

  // Custom Styles
  customStyles: FormControl<string>;
  detailedSummaryCustomCss: FormControl<string>;
}

/**
 * Tab configuration for the settings interface
 */
export interface SettingsTab {
  id: string;
  label: string;
  icon: string;
  component?: string;
}

/**
 * Default component configurations
 */
export const DEFAULT_DETAILED_SUMMARY_COMPONENTS: Omit<
  DetailedSummaryComponent,
  "Order" | "Enabled"
>[] = [
  { Label: "Detailed Summary", Identifier: "detailed-summary" },
  { Label: "Add Product", Identifier: "add-product" },
  { Label: "Payment Method", Identifier: "payment-method" },
  { Label: "Shipping & Delivery", Identifier: "shipping-delivery" },
  { Label: "Change Shipping Address", Identifier: "change-shipping-address" },
  { Label: "Coupons", Identifier: "coupons" },
  { Label: "Update Coupon", Identifier: "update-coupon" },
  { Label: "Order Totals Summary", Identifier: "order-totals-summary" },
  { Label: "Order Notes", Identifier: "order-notes" },
];

/**
 * Default tab configuration
 */
export const DEFAULT_SETTINGS_TABS: SettingsTab[] = [
  { id: "general", label: "General Settings", icon: "pi-cog" },
  { id: "appearance", label: "Appearance & Branding", icon: "pi-palette" },
  { id: "content", label: "Content Management", icon: "pi-file-edit" },
  { id: "advanced", label: "Advanced Configuration", icon: "pi-sliders-h" },
];
